import { useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { QueryKey } from "@/query/Query";

/**
 * Refreshes the change-info lines and history overlays of ONE client after a save.
 *
 * Both queries use `staleTime: Infinity` because the backend recomputes the whole section diff per
 * call, so nothing would refetch them on its own. Any mutation can be the save that changed any
 * section — a mutation fired on one page can create data of another (the Overview's
 * Rückruf-Ambulanz button creates a case note), so ALL of this client's sections are invalidated —
 * and with them every entry of a section whose history is scoped per entry (the Vermerke page) —
 * not only the one currently shown; the rest merely go stale and refetch when next viewed.
 *
 * The invalidation is scoped to this client: a save never touches another client's cached history.
 * Consequence worth knowing: a change made to a *different* client in the same session (a creditor
 * merge supersedes claims across clients) does not invalidate that client's cached line, so it can
 * show a stale timestamp until the cache is garbage-collected.
 */
export function useInvalidateChangeHistoryOnSave(clientId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId) {
      return;
    }

    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientChangeInfo, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientChangeHistory, clientId] });
    });
  }, [queryClient, clientId]);
}
